import { crudPage } from '../ui/crud.js';
import { loader } from '../ui/loader.js';
import { badgeStatus } from '../ui/badges.js';
import { el, clear, fmtDataHora, formatarNome, formatarTitulo } from '../lib/dom.js';
import { renderTabela } from '../ui/table.js';
import { listarSugestoesSetor, aprovarSugestaoSetor, recusarSugestaoSetor, fundirSugestaoSetorExistente } from '../domain/sugestoes.js';
import { toast } from '../lib/toast.js';
import { assinarTabela, pollPeriodico } from '../lib/realtime.js';
import { supabase } from '../config/supabaseClient.js';
import { buscarTodos } from '../lib/db.js';
import { abrirAprovacao } from '../ui/aprovarComSimilar.js';
import { recusarComMotivo } from '../ui/recusarComMotivo.js';
import { abrirModal } from '../ui/modal.js';
import { buscaSelect } from '../ui/buscaSelect.js';

/** Opções de setores cadastrados (ATIVO/INATIVO) para escolher o alvo da fusão. */
async function opcoesSetorAlvo(excetoId) {
  const data = await buscarTodos(() => {
    let qq = supabase.from('setor').select('id, nome, status').in('status', ['ATIVO', 'INATIVO']).order('nome');
    if (excetoId) qq = qq.neq('id', excetoId);
    return qq;
  });
  return (data || []).map((r) => ({ value: r.id, label: r.status === 'ATIVO' ? r.nome : `${r.nome} (inativo)` }));
}

/** Funde o setor cadastrado l com outro (move as visitas e remove l). */
async function fundirSetor(l, aoFeito) {
  const opcoes = await opcoesSetorAlvo(l.id);
  if (!opcoes.length) { toast('Não há outro setor para fundir.', 'info'); return; }
  const picker = buscaSelect({ opcoes, limite: 1000 });
  abrirModal({
    titulo: 'Fundir setor',
    corpo: [
      el('p', { class: 'view__sub', style: 'margin-top:0', text: `As visitas do setor "${l.nome}" (se houver) passam para o setor escolhido, e "${l.nome}" é removido. Use para juntar duplicatas (ex.: grafia diferente).` }),
      el('div', { class: 'campo' }, [el('label', { text: 'Manter (setor que vai ficar):' }), picker.elemento]),
    ],
    acoes: [
      { rotulo: 'Cancelar', classe: 'btn' },
      { rotulo: 'Fundir', classe: 'btn btn--primario', onClick: async () => {
        const alvo = picker.getValue();
        if (!alvo) { toast('Escolha o setor que vai ficar.', 'erro'); return false; }
        const { data: n, error } = await supabase.rpc('fundir_setor', { p_de: l.id, p_para: alvo });
        if (error) { toast('Não foi possível fundir: ' + error.message, 'erro'); return false; }
        toast(n > 0 ? `Fundido. ${n} visita(s) movida(s).` : 'Fundido. (Nenhuma visita estava vinculada ainda.)', 'sucesso'); aoFeito?.(); return true;
      } },
    ],
  });
}

const OPCOES_STATUS = [{ value: 'ATIVO', label: 'Ativo' }, { value: 'INATIVO', label: 'Inativo' }];

async function alternarStatusSetor(l, api) {
  const novo = l.status === 'ATIVO' ? 'INATIVO' : 'ATIVO';
  const { error } = await supabase.from('setor').update({ status: novo }).eq('id', l.id);
  if (error) { toast('Erro: ' + error.message, 'erro'); return; }
  toast(novo === 'INATIVO' ? 'Setor inativado.' : 'Setor reativado.', 'sucesso');
  api.recarregar?.();
}

function excluirSetor(l, api) {
  abrirModal({
    titulo: 'Excluir setor',
    corpo: [el('p', { class: 'view__sub', style: 'margin-top:0', text: `Excluir o setor "${l.nome}"? Ele não tem visitas registradas. Esta ação não pode ser desfeita.` })],
    acoes: [
      { rotulo: 'Cancelar', classe: 'btn' },
      { rotulo: 'Excluir', classe: 'btn btn--perigo', onClick: async () => {
        const { error } = await supabase.from('setor').delete().eq('id', l.id);
        if (error) { toast('Não foi possível excluir: ' + error.message, 'erro'); return false; }
        toast('Setor excluído.', 'sucesso'); api.recarregar?.(); return true;
      } },
    ],
  });
}

const crud = crudPage({
  titulo: 'Setores', singular: 'setor', semCabecalho: true,
  subtitulo: 'Setores e serviços do hospital (Resolução SEI 45). A grafia é padronizada automaticamente.',
  tabela: 'setor', ordenarPor: 'nome', asc: true,
  colunas: [
    { chave: 'nome', rotulo: 'Setor' },
    { chave: 'visitas', rotulo: 'Visitas', render: (l) => (l.visitas == null ? '—' : String(l.visitas)) },
    { chave: 'status', rotulo: 'Status', render: (l) => badgeStatus(l.status) },
  ],
  buscarEm: ['nome'],
  enriquecer: async (linhas) => {
    let mapa = null;
    try {
      const { data, error } = await supabase.rpc('setor_visitas');
      if (!error) { mapa = {}; (data || []).forEach((r) => { mapa[r.setor_id] = Number(r.total); }); }
    } catch (e) { /* RPC ausente (0047 nao rodada): contagem desconhecida */ }
    linhas.forEach((l) => { l.visitas = mapa ? (mapa[l.id] || 0) : null; });
  },
  acoesLinha: (l, api) => {
    const out = [];
    const ativo = l.status === 'ATIVO';
    out.push(el('button', { class: 'btn', title: ativo ? 'Inativar setor' : 'Reativar setor', 'aria-label': ativo ? 'Inativar' : 'Reativar', onClick: () => alternarStatusSetor(l, api) }, [el('i', { class: ativo ? 'ph ph-prohibit' : 'ph ph-arrow-counter-clockwise' })]));
    out.push(el('button', { class: 'btn', title: 'Fundir com outro setor (juntar duplicata)', 'aria-label': 'Fundir', onClick: () => fundirSetor(l, () => api.recarregar?.()) }, [el('i', { class: 'ph ph-git-merge' })]));
    if (l.visitas === 0) out.push(el('button', { class: 'btn btn--perigo', title: 'Excluir setor (sem visitas)', 'aria-label': 'Excluir', onClick: () => excluirSetor(l, api) }, [el('i', { class: 'ph ph-trash' })]));
    return out;
  },
  campos: [
    { chave: 'nome', rotulo: 'Nome do setor', obrigatorio: true, normalizar: formatarTitulo },
    { chave: 'status', rotulo: 'Status', tipo: 'select', opcoes: OPCOES_STATUS, padrao: 'ATIVO' },
  ],
});

export const paginaSetores = {
  async render(container) {
    container.innerHTML = '';
    container.append(
      el('h1', { class: 'view__titulo', text: 'Setores' }),
      el('p', { class: 'view__sub', text: 'Setores e serviços do hospital. Sugestões da recepção entram em "Pendentes" para você aprovar.' }),
    );

    const badge = el('span', { class: 'aba__contador', text: '…' });
    const btnCad = el('button', { class: 'aba aba--ativa', onClick: () => trocar('cad') }, 'Cadastrados');
    const btnPend = el('button', { class: 'aba', onClick: () => trocar('pend') }, [el('span', { text: 'Pendentes ' }), badge]);
    const divCad = el('div');
    const divPend = el('div', { style: 'display:none' });
    container.append(el('div', { class: 'abas' }, [btnCad, btnPend]), divCad, divPend);

    crud.render(divCad);

    let pendFeito = false;
    function trocar(a) {
      btnCad.classList.toggle('aba--ativa', a === 'cad');
      btnPend.classList.toggle('aba--ativa', a === 'pend');
      divCad.style.display = a === 'cad' ? '' : 'none';
      divPend.style.display = a === 'pend' ? '' : 'none';
      if (a === 'pend' && !pendFeito) { pendFeito = true; desenharPend(); }
    }

    async function fundirSugSetor(l) {
      const opcoes = await opcoesSetorAlvo(null);
      if (!opcoes.length) { toast('Não há setor cadastrado para fundir.', 'info'); return; }
      const picker = buscaSelect({ opcoes, limite: 1000 });
      abrirModal({
        titulo: 'Fundir sugestão com setor existente',
        corpo: [
          el('p', { class: 'view__sub', style: 'margin-top:0', text: `A sugestão "${l.nome}" será tratada como o mesmo que o setor escolhido — nenhum setor novo é criado e a sugestão sai dos pendentes (quem sugeriu vê como aprovada).` }),
          el('div', { class: 'campo' }, [el('label', { text: 'Setor existente (que vai ficar):' }), picker.elemento]),
        ],
        acoes: [
          { rotulo: 'Cancelar', classe: 'btn' },
          { rotulo: 'Fundir', classe: 'btn btn--primario', onClick: async () => {
            if (!picker.getValue()) { toast('Escolha o setor.', 'erro'); return false; }
            const { erro } = await fundirSugestaoSetorExistente(l.id);
            if (erro) { toast('Erro: ' + erro, 'erro'); return false; }
            toast('Sugestão fundida com o setor existente.', 'sucesso'); desenharPend(); return true;
          } },
        ],
      });
    }

    async function desenharPend() {
      clear(divPend);
      const box = el('div');
      divPend.append(el('p', { class: 'view__sub', text: 'Setores sugeridos pela recepção, aguardando aprovação. Ao aprovar, o setor é criado.' }), box);
      box.append(loader());
      const linhas = await listarSugestoesSetor();
      badge.textContent = String(linhas.length);
      clear(box);
      if (!linhas.length) { box.append(el('p', { class: 'view__sub', text: 'Nenhuma sugestão pendente.' })); return; }
      renderTabela(box, {
        colunas: [
          { chave: 'nome', rotulo: 'Setor sugerido' },
          { chave: 'motivo', rotulo: 'Motivo', render: (l) => l.motivo || '' },
          { chave: 'operador_nome', rotulo: 'Sugerido por', render: (l) => formatarNome(l.operador_nome || '') },
          { chave: 'sugerido_em', rotulo: 'Quando', render: (l) => fmtDataHora(l.sugerido_em) },
          { chave: '_acoes', rotulo: '', ordenavel: false, render: (l) => el('div', { style: 'display:flex;gap:6px;justify-content:flex-end' }, [
            el('button', { class: 'btn btn--primario', title: 'Aprovar', 'aria-label': 'Aprovar', onClick: async () => {
              const existentes = await buscarTodos(() => supabase.from('setor').select('id, nome'));
              abrirAprovacao({
                titulo: 'Aprovar setor', valorInicial: l.nome, existentes, getNome: (x) => x.nome, rotuloConfirmar: 'Aprovar',
                aoConfirmar: async (nomeFinal) => {
                  const { erro } = await aprovarSugestaoSetor(l, nomeFinal);
                  if (erro) { toast('Erro: ' + erro, 'erro'); return false; }
                  toast('Setor criado.', 'sucesso'); crud.recarregar?.(); desenharPend(); return true;
                },
              });
            } }, [el('i', { class: 'ph ph-check' })]),
            el('button', { class: 'btn', title: 'Fundir com um setor já cadastrado (a sugestão é duplicata)', 'aria-label': 'Fundir', onClick: () => fundirSugSetor(l) }, [el('i', { class: 'ph ph-git-merge' })]),
            el('button', { class: 'btn', title: 'Recusar', 'aria-label': 'Recusar', onClick: () => recusarComMotivo(async (motivo) => {
              const { erro } = await recusarSugestaoSetor(l.id, motivo);
              if (erro) { toast('Erro: ' + erro, 'erro'); return false; }
              toast('Sugestão recusada.', 'info'); desenharPend(); return true;
            }) }, [el('i', { class: 'ph ph-x' })]),
          ]) },
        ],
        linhas, buscarEm: ['nome', 'motivo', 'operador_nome'], porPagina: 10,
      });
    }

    listarSugestoesSetor().then((l) => { badge.textContent = String(l.length); });
    const aoMudarPend = () => { if (pendFeito) desenharPend(); else listarSugestoesSetor().then((l) => { badge.textContent = String(l.length); }); };
    assinarTabela('setor_sugestao', aoMudarPend);
    pollPeriodico(aoMudarPend);
  },
};